import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DotsThree, WarningCircle } from "@phosphor-icons/react";
import { IconButton, TextField } from "../../design-system";
import { ChevronRight, Plus } from "../../pages/overview/icons";
import { friendlyError, invalidateQueries } from "../../analytics";
import {
  trackApiKeyActionPressed,
  trackApiKeyDeleted,
  trackButtonPressed,
  trackWorkspaceSwitched,
} from "../../analytics/tracking";
import { storeWorkspaceId } from "../../workspaces/storage";
import {
  useAction,
  useAllApiKeys,
  useIdentityClient,
  type ApiKey,
  type Workspace,
  type WorkspaceKeys,
} from "../../identity";
import { useWorkspace } from "../../workspaces";
import { PanelSkeleton, QueryBoundary } from "../../components/dashboard";
import { Popover } from "../../components/controls/Popover";
import { CreateKeyDialog, RevealValue } from "./CreateKeyDialog";
import { KeysModal, ModalButton } from "./KeysModal";

function maskedValue(value: string): string {
  return `…${value.slice(-4)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/* API keys across ALL the viewer's workspaces, grouped per workspace (keys
 * are workspace credentials — a flat list would invite grabbing the wrong
 * one). The active workspace's group comes first. Create (with optional
 * expiry), rename, rotate, revoke; values are masked in the tables and shown
 * once — copyable — only in the create/rotate reveal pane. */
export default function ApiKeysPage() {
  const client = useIdentityClient();
  const navigate = useNavigate();
  const { workspace: active, workspaces } = useWorkspace();
  const [version, setVersion] = useState(0);

  const switchTo = (id: string) => {
    if (id === active.id) return;

    trackWorkspaceSwitched(active.id, id);
    storeWorkspaceId(id);
    // Stay on this page in the target workspace's context.
    navigate(`/w/${encodeURIComponent(id)}/settings/api-keys`);
  };

  // Active workspace's group first; the rest keep the switcher's order.
  const ordered = [active, ...workspaces.filter((w) => w.id !== active.id)];
  // When every workspace still carries the provisioning default name, N
  // identical "Default workspace" titles disambiguate nothing — show only the
  // first (active) workspace's keys, exactly like a single-workspace org.
  const allDefaultNames = workspaces.every(
    (w) => w.name.trim().toLowerCase() === "default workspace",
  );
  const visible = allDefaultNames ? ordered.slice(0, 1) : ordered;
  // A single visible workspace has nothing to disambiguate — drop the
  // per-group title and the switch affordance. With more than one, keep the
  // titled groups and let a group title switch to that workspace.
  const multiWorkspace = visible.length > 1;
  const groups = useAllApiKeys(
    visible.map((w) => w.id),
    version,
  );
  // Evict every cached keys entry (all versions/orderings) so a remount
  // within the dedupe window can't serve pre-mutation data — a just-created
  // key must never vanish, a revoked one must never resurface (same pattern
  // as MembersPage).
  const refresh = () => {
    invalidateQueries("identity:keys:");
    setVersion((v) => v + 1);
  };

  // Create dialog: opened from the page header (defaults to the active
  // workspace) or from a group header (pre-picks that workspace).
  const [createIn, setCreateIn] = useState<string | null>(null);
  // Collapsed workspace groups (multi-workspace only), by workspace id. All
  // groups start collapsed so the page opens as a scannable list of workspaces.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(workspaces.map((w) => w.id)),
  );
  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  const [renaming, setRenaming] = useState<ApiKey | null>(null);
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [revealed, setRevealed] = useState<ApiKey | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const rotate = useAction(async (key: ApiKey) => {
    const next = await client.rotateApiKey(key.id);
    setRevealed(next);
  }, "rotateApiKey");
  const revoke = useAction(
    (key: ApiKey) => client.revokeApiKey(key.id),
    "revokeApiKey",
  );

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      {/* Creation lives on the per-group "Create key" chips (each targets its
          workspace), so the page header is just the title. */}
      <h1
        className="cursor-default py-1 text-[15px] font-normal leading-none"
        style={{ color: "var(--text)" }}
      >
        API keys
      </h1>

      {actionError && (
        <div
          className="rounded-[5px] px-3 py-2 text-[12px]"
          style={{
            background: "var(--tint-crimson)",
            border: "1px solid var(--border-2)",
            color: "var(--val-crimson)",
          }}
        >
          {actionError}
        </div>
      )}

      <QueryBoundary state={groups} skeleton={<PanelSkeleton rows={6} />}>
        {(rows) => (
          <div className="flex flex-col gap-6">
            {rows.map((group) => (
              <WorkspaceGroup
                key={group.workspaceId}
                group={group}
                workspace={visible.find((w) => w.id === group.workspaceId)}
                isActive={group.workspaceId === active.id}
                showTitle={multiWorkspace}
                // Only multi-workspace groups collapse (a single group has no
                // header to collapse from).
                collapsible={multiWorkspace}
                collapsed={collapsed.has(group.workspaceId)}
                onToggleCollapse={() => toggleCollapsed(group.workspaceId)}
                onSwitch={
                  multiWorkspace && group.workspaceId !== active.id
                    ? () => switchTo(group.workspaceId)
                    : undefined
                }
                onCreate={() => setCreateIn(group.workspaceId)}
                onRename={setRenaming}
                onRotate={setRotating}
                onRevoke={setRevoking}
              />
            ))}
          </div>
        )}
      </QueryBoundary>

      <CreateKeyDialog
        open={createIn !== null}
        workspaces={visible}
        initialWorkspaceId={createIn ?? active.id}
        onClose={() => setCreateIn(null)}
        onCreated={refresh}
      />

      <RenameKeyDialog
        apiKey={renaming}
        onClose={() => setRenaming(null)}
        onRenamed={refresh}
      />

      <KeysModal
        open={rotating !== null}
        title="Rotate API key"
        description={
          rotating
            ? `“${rotating.name}” gets a new value; the current value stops working immediately.`
            : undefined
        }
        busy={rotate.busy}
        onClose={() => setRotating(null)}
        footer={
          <>
            <ModalButton
              disabled={rotate.busy}
              onClick={() => setRotating(null)}
            >
              Cancel
            </ModalButton>
            <ModalButton
              variant="brand"
              disabled={rotate.busy}
              onClick={async () => {
                if (!rotating) return;

                const ok = await rotate.run(rotating);
                if (ok) {
                  trackApiKeyActionPressed("rotate", rotating.workspaceId);
                  refresh();
                } else {
                  setActionError(friendlyError(rotate.error).message);
                }

                setRotating(null);
              }}
            >
              {rotate.busy ? "Working…" : "Rotate key"}
            </ModalButton>
          </>
        }
      />

      <KeysModal
        open={revoking !== null}
        title="Revoke API key"
        description={
          revoking
            ? `“${revoking.name}” stops authenticating immediately. This can't be undone.`
            : undefined
        }
        busy={revoke.busy}
        onClose={() => setRevoking(null)}
        footer={
          <>
            <ModalButton
              disabled={revoke.busy}
              onClick={() => setRevoking(null)}
            >
              Cancel
            </ModalButton>
            <ModalButton
              variant="danger"
              disabled={revoke.busy}
              onClick={async () => {
                if (!revoking) return;

                const ok = await revoke.run(revoking);
                if (ok) {
                  trackApiKeyDeleted(revoking.workspaceId);
                  refresh();
                } else {
                  setActionError(friendlyError(revoke.error).message);
                }

                setRevoking(null);
              }}
            >
              {revoke.busy ? "Working…" : "Revoke key"}
            </ModalButton>
          </>
        }
      />

      {/* Reveal pane for a rotated key (create has its own, inside its dialog). */}
      <KeysModal
        open={revealed !== null}
        title="Key rotated"
        description="Copy the new value now and update every client using this key."
        onClose={() => setRevealed(null)}
        footer={
          <ModalButton variant="brand" onClick={() => setRevealed(null)}>
            Done
          </ModalButton>
        }
      >
        {revealed && <RevealValue value={revealed.value} />}
      </KeysModal>
    </div>
  );
}

function WorkspaceGroup({
  group,
  workspace,
  isActive,
  showTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
  onSwitch,
  onCreate,
  onRename,
  onRotate,
  onRevoke,
}: {
  group: WorkspaceKeys;
  workspace?: Workspace;
  isActive: boolean;
  /** Hide the per-group title (single-workspace org — nothing to disambiguate). */
  showTitle: boolean;
  /** Whether this group can collapse (multi-workspace only). */
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Switch to this workspace (multi-workspace, non-active groups only). */
  onSwitch?: () => void;
  onCreate: () => void;
  onRename: (key: ApiKey) => void;
  onRotate: (key: ApiKey) => void;
  onRevoke: (key: ApiKey) => void;
}) {
  const name = workspace?.name ?? group.workspaceId;
  const keys = group.keys ?? [];
  const keyCount = keys.length;
  const isCollapsed = collapsible && collapsed;

  // In-group search (name match) — only worth the chrome once the list is
  // longer than the scroll viewport's worth of rows.
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = q
    ? keys.filter((k) => k.name.toLowerCase().includes(q))
    : keys;
  const searchable = !group.error && keyCount > 5;

  // Right-side controls: search (expanded, long lists only) + the create chip.
  // Shared between the titled multi-workspace header and the single-workspace
  // toolbar so both layouts offer the same affordances.
  const controls = (
    <div className="flex items-center gap-2.5">
      {searchable && !isCollapsed && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search keys"
          aria-label={`Search keys in ${name}`}
          className="h-[30px] w-[200px] rounded-[5px] border border-[var(--border-2)] bg-transparent px-2.5 text-[12px] text-[var(--text)] placeholder:text-[var(--text-muted)] focus:outline-none"
        />
      )}
      <button
        onClick={() => {
          trackButtonPressed("Create key", "apiKey");
          onCreate();
        }}
        className="flex items-center gap-1.5 rounded-[5px] border border-[var(--border-2)] px-2.5 py-1.5 text-[12px] text-[var(--text)] transition-colors hover:bg-[var(--hover)]"
      >
        <Plus size={12} /> Create key
      </button>
    </div>
  );

  // Only expose a labelled region when the title shows — a single-workspace org
  // has one implicit group, so a "API keys for X" landmark just adds noise.
  return (
    <section aria-label={showTitle ? `API keys for ${name}` : undefined}>
      {showTitle && (
        <div className="mb-3 flex items-center gap-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {collapsible && (
              <button
                onClick={onToggleCollapse}
                aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${name}`}
                aria-expanded={!isCollapsed}
                className="flex size-5 flex-shrink-0 items-center justify-center text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
              >
                <ChevronRight
                  size={14}
                  className="transition-transform"
                  style={{
                    transform: isCollapsed ? "none" : "rotate(90deg)",
                  }}
                />
              </button>
            )}
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{ background: workspace?.color ?? "var(--spoke-mute)" }}
            />
            {onSwitch ? (
              // Non-active workspace: the title switches to it.
              <button
                onClick={onSwitch}
                className="group flex items-center gap-1.5 text-left"
                aria-label={`Switch to ${name}`}
              >
                <span className="text-[15px] leading-none text-[var(--text)] group-hover:underline">
                  {name}
                </span>
                <ChevronRight
                  size={13}
                  className="text-[var(--text-muted)] transition-transform group-hover:translate-x-0.5"
                />
              </button>
            ) : (
              <span className="cursor-default text-[15px] leading-none text-[var(--text)]">
                {name}
              </span>
            )}
            {!group.error && (
              <span className="text-[15px] leading-none text-[var(--text-muted)]">
                ({keyCount})
              </span>
            )}
            {isActive && (
              <span
                className="tw-mono rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
                style={{
                  borderColor: "var(--border-2)",
                  color: "var(--text-muted)",
                }}
              >
                Current workspace
              </span>
            )}
          </div>
          {controls}
        </div>
      )}
      {!showTitle && (
        <div className="mb-3 flex items-center justify-end">{controls}</div>
      )}

      {isCollapsed ? null : group.error ? (
        <div
          className="flex items-start gap-2.5 rounded-[5px] px-3 py-3 text-[12px]"
          style={{
            background: "var(--tint-crimson)",
            border: "1px solid var(--border-2)",
            color: "var(--val-crimson)",
          }}
        >
          <WarningCircle className="mt-0.5 size-4 shrink-0" />
          <div>
            Couldn’t load this workspace’s keys:{" "}
            {friendlyError(group.error).message}
          </div>
        </div>
      ) : keyCount === 0 ? (
        <div
          className="rounded-[5px] px-5 py-4 text-[12px] text-[var(--text-muted)]"
          style={{ border: "1px dashed var(--border-2)" }}
        >
          No API keys in this workspace yet.
        </div>
      ) : visible.length === 0 ? (
        <div
          className="rounded-[5px] px-5 py-4 text-[12px] text-[var(--text-muted)]"
          style={{ border: "1px dashed var(--border-2)" }}
        >
          No keys match “{query.trim()}”.
        </div>
      ) : (
        // Capped at ~6 rows; longer lists scroll in place (search narrows).
        <div
          className="flex flex-col gap-2 overflow-y-auto pr-1"
          style={{ maxHeight: `${MAX_LIST_PX}px` }}
        >
          {visible.map((key, i) => (
            <KeyRow
              key={key.id}
              apiKey={key}
              floatDelay={0.1 + Math.min(i, 6) * 0.05}
              onRename={() => onRename(key)}
              onRotate={() => onRotate(key)}
              onRevoke={() => onRevoke(key)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* Scroll cap for a group's key list: ~6 rows (57px row + 8px gap). */
const MAX_LIST_PX = 6 * 57 + 5 * 8;

/* Shared column template so the bordered rows align as one list:
 * name+masked value | status | expiry | ⋯ */
const ROW_GRID =
  "grid grid-cols-[minmax(180px,1.5fr)_minmax(110px,1fr)_110px_32px] items-center gap-4";

/* One key as an individually bordered rounded row (the overview roster's row
 * language): name with the masked value underneath, a status pill, the expiry
 * as a mono stat, and the actions menu. */
function KeyRow({
  apiKey,
  floatDelay,
  onRename,
  onRotate,
  onRevoke,
}: {
  apiKey: ApiKey;
  /** Entrance stagger (seconds). */
  floatDelay: number;
  onRename: () => void;
  onRotate: () => void;
  onRevoke: () => void;
}) {
  const active = apiKey.status === "ACTIVE";
  return (
    <div
      className={`tw-float-up rounded-[5px] border border-[var(--border)] px-5 py-[13px] transition-colors hover:bg-[var(--hover)] ${ROW_GRID}`}
      style={{ "--float-delay": `${floatDelay}s` } as React.CSSProperties}
    >
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium leading-tight text-[var(--text)]">
          {apiKey.name}
        </p>
        {/* Masked, deliberately not copyable — the full value is only handed
            out in the create/rotate reveal pane. */}
        <code className="tw-mono text-[11px] leading-tight text-[var(--text-muted)]">
          {maskedValue(apiKey.value)}
        </code>
      </div>
      {active ? (
        <span
          className="tw-mono justify-self-start rounded-full px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{
            // Same fixed scheme as the Savings card: mint ground, green ink,
            // identical in both themes.
            background: "var(--savings-card-bg)",
            color: "var(--savings-fg)",
          }}
        >
          Active
        </span>
      ) : (
        <span
          className="tw-mono justify-self-start rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{ borderColor: "var(--border-2)", color: "var(--text-muted)" }}
        >
          {apiKey.status.toLowerCase()}
        </span>
      )}
      <div className="flex flex-col items-end gap-0.5">
        <span
          className="tw-mono text-[11px] font-medium leading-none tabular-nums"
          style={{ color: "var(--text)" }}
        >
          {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : "Never"}
        </span>
        <span
          className="tw-mono text-[10px] uppercase leading-none tracking-[0.25px]"
          style={{ color: "var(--text-muted)" }}
        >
          Expires
        </span>
      </div>
      <div className="justify-self-end">
        {/* Portaled: the row lives in a scroll-capped list (and its entrance
            animation is a stacking context), so an in-place panel would be
            clipped by the container or covered by sibling rows. */}
        <Popover
          align="end"
          portal
          trigger={
            <IconButton
              variant="ghost"
              size="sm"
              icon={<DotsThree />}
              aria-label={`Actions for ${apiKey.name}`}
            />
          }
        >
          {(close) => (
            <div className="flex flex-col">
              <MenuItem
                onClick={() => {
                  close();
                  onRename();
                }}
              >
                Rename…
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onRotate();
                }}
              >
                Rotate…
              </MenuItem>
              <MenuItem
                destructive
                onClick={() => {
                  close();
                  onRevoke();
                }}
              >
                Revoke…
              </MenuItem>
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
}

function MenuItem({
  children,
  destructive = false,
  onClick,
}: {
  children: React.ReactNode;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 items-center px-2.5 text-left text-[12px] hover:bg-[var(--hover)]"
      style={{ color: destructive ? "var(--val-crimson)" : "var(--text)" }}
    >
      {children}
    </button>
  );
}

function RenameKeyDialog({
  apiKey,
  onClose,
  onRenamed,
}: {
  apiKey: ApiKey | null;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const client = useIdentityClient();
  const [name, setName] = useState<string | null>(null);
  const rename = useAction(
    (keyId: string, keyName: string) => client.renameApiKey(keyId, keyName),
    "renameApiKey",
  );

  // Seed the field from the key being renamed (null = untouched).
  const value = name ?? apiKey?.name ?? "";

  const closeAndReset = () => {
    setName(null);
    rename.reset();
    onClose();
  };

  const submit = async () => {
    const trimmed = value.trim();
    if (!apiKey || !trimmed) return;

    if (await rename.run(apiKey.id, trimmed)) {
      trackApiKeyActionPressed("rename", apiKey.workspaceId);
      onRenamed();
      closeAndReset();
    }
  };

  return (
    <KeysModal
      open={apiKey !== null}
      title="Rename API key"
      busy={rename.busy}
      onClose={closeAndReset}
      footer={
        <>
          <ModalButton disabled={rename.busy} onClick={closeAndReset}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="brand"
            disabled={rename.busy || !value.trim()}
            onClick={submit}
          >
            {rename.busy ? "Saving…" : "Save name"}
          </ModalButton>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          label="Name"
          value={value}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          error={rename.error ? friendlyError(rename.error).message : undefined}
        />
      </form>
    </KeysModal>
  );
}
